import React, { useEffect, useState } from 'react';
import {
    Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, MenuItem, TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { SupportController } from "../../../controllers/supportController";

const CATEGORIES = ["ORDER", "RETURN", "REFUND", "PAYMENT", "PRODUCT", "PLATFORM", "OTHER"];

const statusColor = (s: string) =>
    s === "RESOLVED" || s === "CLOSED" ? "success" : s === "WAITING" ? "warning" : "info";

function HelpAndSupport() {
    const { typography } = theme;
    const [tickets, setTickets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState<any | null>(null); // open thread
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);

    // raise dialog
    const [raiseOpen, setRaiseOpen] = useState(false);
    const [form, setForm] = useState({ subject: "", category: "ORDER", message: "" });

    const load = async () => {
        setLoading(true);
        try {
            const res: any = await SupportController.getMyTickets({ page: 0, size: 50 });
            setTickets(res?.data?.data?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openThread = async (id: string) => {
        try {
            const res: any = await SupportController.getTicket(id);
            setActive(res?.data?.data);
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to open ticket");
        }
    };

    const submitRaise = async () => {
        if (!form.subject.trim() || !form.message.trim()) {
            toast.error("Subject and message are required");
            return;
        }
        setBusy(true);
        try {
            await SupportController.createTicket(form);
            toast.success("Ticket raised");
            setRaiseOpen(false);
            setForm({ subject: "", category: "ORDER", message: "" });
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to raise ticket");
        } finally {
            setBusy(false);
        }
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            const res: any = await SupportController.reply(active.id, reply.trim());
            setActive(res?.data?.data);
            setReply("");
            load();
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to send reply");
        } finally {
            setBusy(false);
        }
    };

    // ----- thread view -----
    if (active) {
        return (
            <FaiBox className={"order"}>
                <Box className="flex justify-between items-center" mb={2}>
                    <Button onClick={() => setActive(null)}>← Back to tickets</Button>
                    <Chip label={active.status} color={statusColor(active.status) as any} size="small" />
                </Box>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
                    {active.subject}
                </FaiTypography>
                <FaiTypography variant={"caption"}>
                    {active.ticketNumber} · {active.category}
                </FaiTypography>
                <Card sx={{ p: 2, mt: 2, maxHeight: 420, overflowY: "auto" }}>
                    {(active.messages || []).map((m: any, i: number) => {
                        const mine = m.authorRole === "ROLE_MERCHANT";
                        return (
                            <Box key={i} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1.5 }}>
                                <Box sx={{ maxWidth: "70%", p: 1.5, borderRadius: 2, bgcolor: mine ? "#E09145" : "#F0F0F0", color: mine ? "#fff" : "#000" }}>
                                    <FaiTypography variant={"caption"} sx={{ fontWeight: 600 }}>
                                        {m.authorRole === "ROLE_ADMIN" ? "Support" : "You"}
                                    </FaiTypography>
                                    <FaiTypography variant={"body2"}>{m.body}</FaiTypography>
                                </Box>
                            </Box>
                        );
                    })}
                </Card>
                {active.status !== "CLOSED" && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        <TextField fullWidth size="small" placeholder="Type a reply…" value={reply} onChange={(e) => setReply(e.target.value)} />
                        <Button variant="contained" disabled={busy} onClick={sendReply}>Send</Button>
                    </Box>
                )}
            </FaiBox>
        );
    }

    // ----- list view -----
    return (
        <FaiBox className={"order"}>
            <Box className="flex justify-between items-center" mb={"21px"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
                    Help & Support
                </FaiTypography>
                <Button variant="contained" onClick={() => setRaiseOpen(true)}>Raise a ticket</Button>
            </Box>

            {loading ? (
                <Box className="flex justify-center p-6"><CircularProgress /></Box>
            ) : tickets.length === 0 ? (
                <FaiTypography>No tickets yet. Raise one to get help.</FaiTypography>
            ) : (
                tickets.map((t) => (
                    <Card key={t.id} sx={{ p: 2, mb: 1.5, cursor: "pointer" }} onClick={() => openThread(t.id)}>
                        <Box className="flex justify-between items-center">
                            <Box>
                                <FaiTypography variant={"body1"} sx={{ fontWeight: 600 }}>{t.subject}</FaiTypography>
                                <FaiTypography variant={"caption"}>{t.ticketNumber} · {t.category}</FaiTypography>
                            </Box>
                            <Chip label={t.status} color={statusColor(t.status) as any} size="small" />
                        </Box>
                    </Card>
                ))
            )}

            <Dialog open={raiseOpen} onClose={() => setRaiseOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Raise a ticket</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <TextField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} fullWidth />
                    <TextField label="Category" select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} fullWidth>
                        {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField label="Describe your issue" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} fullWidth multiline minRows={4} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRaiseOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={submitRaise}>Submit</Button>
                </DialogActions>
            </Dialog>
        </FaiBox>
    );
}

export default HelpAndSupport;
